'use client';

import { Suspense, useEffect, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { HeaderServer } from './HeaderServer';
import { useSidebarStore } from '@/src/sections/social/store/sidebar-store';
import HomeGamesSearch from './HomeGamesSearch';
import { Icon } from '../ui';
import Image from 'next/image';
import SearchInput from '@/src/components/layout/SearchInput';
import Link from 'next/link';
import { AvatarWithOptions } from '../ui/AvatarWithOptions';

const ALLOWED_SEARCH_PATHS = [
  '/',
  '/explore-stories',
  '/browse-projects',
  '/library',
];

function isSearchAllowed(pathname: string | null) {
  if (!pathname) return false;
  return ALLOWED_SEARCH_PATHS.some((p) =>
    p === '/' ? pathname === '/' : pathname.startsWith(p),
  );
}

export default function HeaderClient() {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { opened, toggleOpened } = useSidebarStore();
  const isHome = pathname === '/';
  const isAboutPage = pathname?.includes('/about');
  if (isAboutPage) return null;

  const showSearch = isSearchAllowed(pathname);
  const tab = searchParams.get('tab') || 'game';

  const urlSearch = searchParams.get('search') || '';
  const [inputValue, setInputValue] = useState(urlSearch);

  useEffect(() => {
    setInputValue(urlSearch);
  }, [urlSearch]);

  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, []);

  const updateUrlSearch = (next: string) => {
    const params = new URLSearchParams(searchParams.toString());
    if (next) params.set('search', next);
    else params.delete('search');

    router.replace(`?${params.toString()}`);
  };

  const handleChange = (next: string) => {
    setInputValue(next);

    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      updateUrlSearch(next.trim());
    }, 600);
  };

  return (
    <HeaderServer className={opened ? 'block' : 'hidden'}>
      <div className="hidden md:inline-block">
        {isHome ? (
          <HomeGamesSearch />
        ) : (
          showSearch && (
            <SearchInput value={inputValue} onChange={handleChange} tab={tab} />
          )
        )}
      </div>

      <div className="md:hidden flex items-center justify-between w-full">
        <Icon name="burger" className="size-8" onClick={toggleOpened} />
        {isHome ? (
          <HomeGamesSearch />
        ) : showSearch ? (
          <SearchInput value={inputValue} onChange={handleChange} tab={tab} />
        ) : (
          <div className="w-[calc(50%+20px)] flex justify-between items-center">
            <Link href="/">
              <Image
                src="/icons/logoHero.png"
                alt="logo"
                width={40}
                height={40}
                priority
                className="object-contain"
              />
            </Link>
            <AvatarWithOptions />
          </div>
        )}
      </div>
    </HeaderServer>
  );
}

export function Header() {
  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b">
      <Suspense fallback={null}>
        <HeaderClient />
      </Suspense>
    </div>
  );
}
